import http, { endpoints } from '../http';
import { flattenCaseDeliveries, mapCollagePriceFromDto, mapLinkOrDocumentMaterialFromDto, mapOrderLineAssetFromDto, } from './helpers';
const efktClient = http.createClient(endpoints.efkt);
const MAX_LIMIT = 100;
/**
 * Named and shaped after datahub's `MaterialApi` (41 call sites in `v2/modules/material/`) so a
 * migrated hook swaps one import, not one contract.
 *
 * `getSimplifiedVisualMaterials`/`getUploadedMaterialForOrderLine` call real Efkt routes. Every method
 * from `sendDownloadInfo` down is **EFKT-MOCK** — no gateway route exists for any of them yet
 * (activate/deactivate, sort/rename, download, archive/restore/recreate, collage, copy, link/document
 * CRUD, batch remove, suggested-names). Each guesses a path under `/api/case/{caseId}/assets/...`,
 * matching this domain's one real route family. Grep `EFKT-MOCK` across this package for every
 * placeholder like this one.
 *
 * **File uploads split in two.** The bytes go up through `AssetsApi.uploadFile` (the Assets multipart
 * saga: initiate → part URLs → progress → complete → status), which is real and read off the backend.
 * Recording the result against an order line is the `registerBatchMaterial`/`sealBatchUploadedMaterial`/
 * `removeBatchMaterial` trio below — EFKT-MOCK, because Efkt's own versions are job-scoped and
 * photographer-authenticated. See each method and the "Known gaps" comment in `../index.ts`.
 */
export default class MaterialApi {
    /**
     * `GET /api/case/{caseId}/deliveries?includeAssets=true&limit=100` — same return shape as
     * datahub's method of the same name (`materials`, `batchList`, `links`), so callers don't need
     * to reshape anything.
     *
     * `limit` is capped at 100 server-side; a case with more than 100 orders is truncated. There is
     * no cursor — worth a BE ask if this ever bites a real branch.
     */
    static async getSimplifiedVisualMaterials(caseId) {
        const response = await efktClient.get(`/api/case/${encodeURIComponent(caseId)}/deliveries`, { params: { includeAssets: true, limit: MAX_LIMIT } });
        return flattenCaseDeliveries(response.data.items);
    }
    /**
     * `GET /api/order-line/{orderLineId}/assets`, bare array. Mirrors datahub's
     * `getUploadedMaterialForOrderLine`, which also returns every asset unfiltered.
     */
    static async getUploadedMaterialForOrderLine(orderLineId) {
        const response = await efktClient.get(`/api/order-line/${encodeURIComponent(orderLineId)}/assets`);
        return response.data.map(dto => mapOrderLineAssetFromDto(dto, orderLineId));
    }
    /**
     * EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.registerBatchMaterial`: record one file the
     * browser just put into storage against the order line, before the batch is sealed.
     *
     * **Why guessed rather than wired to the real saga.** Efkt's upload mechanism does have a
     * completion step (`POST /api/photographer/jobs/{jobId}/uploads/{uploadId}/complete`), but it is
     * job-scoped and photographer-authenticated, and booqly's uploader is the estate agent completing
     * an "upload images" task — it knows a `taskId` and an `orderLineId`, never a `jobId`. This is the
     * order-line-keyed compat twin BE was asked for (`docs/jira/adapt-file-uploader-progress-widget.md`),
     * sitting in the one real route family this domain has.
     */
    static async registerBatchMaterial({ orderlineId, fileName, url }) {
        const body = { fileName, url };
        await efktClient.post(`/api/order-line/${encodeURIComponent(orderlineId)}/assets/uploads`, body);
    }
    /**
     * EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.sealBatchUploadedMaterial`, bare-array body
     * included. Same twin-vs-saga reasoning as `registerBatchMaterial`: on Efkt this is
     * assign-then-submit under `/api/photographer/jobs/{jobId}/...`, which the customer-side actor
     * cannot reach.
     */
    static async sealBatchUploadedMaterial({ orderlineId, uploads }) {
        await efktClient.post(`/api/order-line/${encodeURIComponent(orderlineId)}/assets`, uploads);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.removeBatchMaterial`. */
    static async removeBatchMaterial({ orderlineId, materialId }) {
        await efktClient.delete(`/api/order-line/${encodeURIComponent(orderlineId)}/assets/${encodeURIComponent(materialId)}`);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.sendDownloadInfo`. */
    static async sendDownloadInfo({ caseId, webResolution, materialIds, email }) {
        const body = { webResolution, materialIds, email };
        await efktClient.post(`/api/case/${encodeURIComponent(caseId)}/assets/download-link`, body);
    }
    /**
     * EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.archive`, `{ success }` return included, so
     * `CaseDetailsPageContent`'s call site needs no edit. No way to reflect this back into
     * `ICase.isArchived` yet — that field's mapper always injects `false` regardless (see
     * `../cases/helpers.ts`) — so calling this today would silently not update anything visible.
     */
    static async archive(caseId) {
        await efktClient.post(`/api/case/${encodeURIComponent(caseId)}/assets/archive`);
        return { success: true };
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.recreateMaterials`. Same caveat as `archive`. */
    static async recreateMaterials(caseId) {
        await efktClient.post(`/api/case/${encodeURIComponent(caseId)}/assets/restore`);
        return { success: true };
    }
    /**
     * EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.updateMaterials` (sort order + rename, one
     * full-batch write).
     */
    static async updateMaterials(caseId, assets) {
        const body = { assets };
        await efktClient.post(`/api/case/${encodeURIComponent(caseId)}/assets/sort-order`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.copyMaterials`. */
    static async copyMaterials({ caseId, assetIds, targetCaseId, copyAll }) {
        const body = { assetIds, targetCaseId, copyAll };
        await efktClient.post(`/api/case/${encodeURIComponent(caseId)}/assets/copy`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.getLinkMaterials`. */
    static async getLinkMaterials(caseId) {
        const response = await efktClient.get(`/api/case/${encodeURIComponent(caseId)}/assets/links`);
        return response.data.map(mapLinkOrDocumentMaterialFromDto);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.getDocumentMaterials`. */
    static async getDocumentMaterials(caseId) {
        const response = await efktClient.get(`/api/case/${encodeURIComponent(caseId)}/assets/documents`);
        return response.data.map(mapLinkOrDocumentMaterialFromDto);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.saveLinkAsset`. */
    static async saveLinkAsset({ caseId, assets }) {
        const body = { assets };
        await efktClient.post(`/api/case/${encodeURIComponent(caseId)}/assets/links`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.saveDocumentAsset`. */
    static async saveDocumentAsset({ caseId, assets }) {
        const body = { assets };
        await efktClient.post(`/api/case/${encodeURIComponent(caseId)}/assets/documents`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.deleteLinkAsset` (covers both links and documents). */
    static async deleteLinkOrDocumentAsset(caseId, assetId) {
        await efktClient.delete(`/api/case/${encodeURIComponent(caseId)}/assets/${encodeURIComponent(assetId)}`);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.getSuggestedMaterialNames`. */
    static async getSuggestedMaterialNames(caseId) {
        const response = await efktClient.get(`/api/case/${encodeURIComponent(caseId)}/assets/allowed-names`);
        return response.data.map(({ value }) => value);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.getCollagePrice`. */
    static async getCollagePrice(caseId) {
        const response = await efktClient.get(`/api/case/${encodeURIComponent(caseId)}/assets/collage/pricing`);
        return mapCollagePriceFromDto(response.data);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.submitCollage`. */
    static async submitCollage({ caseId, collageUrl }) {
        const body = { collageUrl };
        await efktClient.post(`/api/case/${encodeURIComponent(caseId)}/assets/collage`, body);
    }
    /**
     * EFKT-MOCK — guessed. Mirrors datahub's `MaterialApi.materialBatchRemove`, the batch-id-scoped
     * delete. Datahub also exposes a second, order-line-id-scoped batch-remove endpoint
     * (`deleteMaterialsAndAssets`, `DELETE /Assets/delete-materials-and-assets`) that the portal's own
     * ⚠ review flag already identified as the *live* one there and this one's sibling as dead code —
     * deliberately mirroring only this one rather than both, so nobody "completes the pair" later.
     */
    static async materialBatchRemove(caseId, batchId) {
        await efktClient.delete(`/api/case/${encodeURIComponent(caseId)}/assets/batches/${encodeURIComponent(batchId)}`);
    }
}
